/**
 * Data hooks for the Brain workspace.
 *
 * React Query owns caching; these name the keys and the shapes so components
 * never build a query key by hand.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from '@/lib/query.client'
import {
  agentsApi,
  dqApi,
  edgeTypesApi,
  explorerApi,
  generatorApi,
  graphsApi,
  matchingApi,
  nodeTypesApi,
  objectsApi,
  pipelineApi,
  recordsApi,
  relationshipsApi,
  searchApi,
  sourcesApi,
  survivorshipApi,
} from '../api/brain.api'
import type { CypherEngine, ModelProposal } from '../types/brain.types'
import { brainKeys, invalidateData, invalidateModel } from './brain.keys'

export { brainKeys, invalidateData, invalidateModel }

// --- graph ------------------------------------------------------------------

export function useGraphs() {
  return useQuery({ queryKey: brainKeys.graphs, queryFn: graphsApi.list })
}

export function useGraphModel(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.model(graphId ?? ''),
    queryFn: () => graphsApi.model(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useGraphStats(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.stats(graphId ?? ''),
    queryFn: () => graphsApi.stats(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useCreateGraph() {
  return useMutation({
    mutationFn: (body: { name: string; description?: string; requirement?: string }) =>
      graphsApi.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: brainKeys.graphs }),
  })
}

export function usePublishGraph(graphId: string) {
  return useMutation({
    mutationFn: (body: { version: string; note?: string }) => graphsApi.publish(graphId, body),
    onSuccess: () => invalidateModel(graphId),
  })
}

// --- the graph explorer ------------------------------------------------------

export function useExplorerStats(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.explorerStats(graphId ?? ''),
    queryFn: () => explorerApi.stats(graphId!),
    enabled: Boolean(graphId),
    // The counts come from a 60-second server cache; refetching them harder
    // than that only spends requests.
    staleTime: 60_000,
  })
}

export function useExplorerLimits(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.explorerLimits(graphId ?? ''),
    queryFn: () => explorerApi.limits(graphId!),
    enabled: Boolean(graphId),
    staleTime: Infinity,
  })
}

export function useQueryLibrary(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.explorerQueries(graphId ?? ''),
    queryFn: () => explorerApi.queries(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useRunCypher(graphId: string) {
  return useMutation({
    mutationFn: (vars: {
      query: string
      parameters?: Record<string, unknown>
      engine?: CypherEngine
    }) => explorerApi.run(graphId, vars.query, vars.parameters ?? {}, vars.engine ?? 'auto'),
    // Every run, refused or not, is history.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: brainKeys.explorerQueries(graphId) }),
  })
}

export function useSaveQuery(graphId: string) {
  return useMutation({
    mutationFn: (body: { name: string; query: string; description?: string }) =>
      explorerApi.save(graphId, body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: brainKeys.explorerQueries(graphId) }),
  })
}

export function useDeleteSavedQuery(graphId: string) {
  return useMutation({
    mutationFn: (queryId: string) => explorerApi.remove(graphId, queryId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: brainKeys.explorerQueries(graphId) }),
  })
}

export function useRebuildProjection(graphId: string) {
  return useMutation({
    mutationFn: () => explorerApi.rebuild(graphId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: brainKeys.explorerStats(graphId) }),
  })
}

// --- node types --------------------------------------------------------------

export function useNodeTypes(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.nodeTypes(graphId ?? ''),
    queryFn: () => nodeTypesApi.list(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useNodeTypeDetail(graphId: string | undefined, nodeTypeId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.nodeType(graphId ?? '', nodeTypeId ?? ''),
    queryFn: () => nodeTypesApi.detail(graphId!, nodeTypeId!),
    enabled: Boolean(graphId && nodeTypeId),
  })
}

export function useNodeTypeMutations(graphId: string) {
  const done = () => invalidateModel(graphId)
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => nodeTypesApi.create(graphId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        nodeTypesApi.update(graphId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => nodeTypesApi.remove(graphId, id),
      onSuccess: done,
    }),
    move: useMutation({
      mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) =>
        nodeTypesApi.move(graphId, id, x, y),
    }),
  }
}

export function usePropertyMutations(graphId: string, nodeTypeId: string) {
  const done = () => invalidateModel(graphId)
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) =>
        nodeTypesApi.createProperty(graphId, nodeTypeId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        nodeTypesApi.updateProperty(graphId, nodeTypeId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => nodeTypesApi.removeProperty(graphId, nodeTypeId, id),
      onSuccess: done,
    }),
  }
}

// --- edge types --------------------------------------------------------------

export function useEdgeTypes(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.edgeTypes(graphId ?? ''),
    queryFn: () => edgeTypesApi.list(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useEdgeTypeMutations(graphId: string) {
  const done = () => invalidateModel(graphId)
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => edgeTypesApi.create(graphId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        edgeTypesApi.update(graphId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => edgeTypesApi.remove(graphId, id),
      onSuccess: done,
    }),
  }
}

// --- dq ----------------------------------------------------------------------

/** The comparison set the condition builder offers, keyed by property type. */
export function useDqConditionCatalogue(graphId: string | undefined) {
  return useQuery({
    queryKey: [...brainKeys.dqOperators(graphId ?? ''), 'conditions'],
    queryFn: () => dqApi.conditions(graphId!),
    enabled: Boolean(graphId),
    staleTime: 30 * 60_000,
  })
}

export function useDqOperators(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.dqOperators(graphId ?? ''),
    queryFn: () => dqApi.operators(graphId!),
    enabled: Boolean(graphId),
    staleTime: 30 * 60_000,
  })
}

export function useDqCompliance(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.dqCompliance(graphId ?? ''),
    queryFn: () => dqApi.compliance(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useDqMutations(graphId: string, nodeTypeId: string) {
  const done = () => invalidateModel(graphId)
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => dqApi.create(graphId, nodeTypeId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        dqApi.update(graphId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({ mutationFn: (id: string) => dqApi.remove(graphId, id), onSuccess: done }),
    reorder: useMutation({
      mutationFn: (orderedIds: string[]) => dqApi.reorder(graphId, nodeTypeId, orderedIds),
      onSuccess: done,
    }),
    test: useMutation({
      mutationFn: (record: Record<string, unknown>) => dqApi.test(graphId, nodeTypeId, record),
    }),
  }
}

// --- matching ----------------------------------------------------------------

export function useMatchMethods(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.matchMethods(graphId ?? ''),
    queryFn: () => matchingApi.methods(graphId!),
    enabled: Boolean(graphId),
    staleTime: 30 * 60_000,
  })
}

export function useMatchMutations(graphId: string, nodeTypeId: string) {
  const done = () => invalidateModel(graphId)
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => matchingApi.create(graphId, nodeTypeId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        matchingApi.update(graphId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => matchingApi.remove(graphId, id),
      onSuccess: done,
    }),
    reorder: useMutation({
      mutationFn: (orderedIds: string[]) => matchingApi.reorder(graphId, nodeTypeId, orderedIds),
      onSuccess: done,
    }),
    test: useMutation({
      mutationFn: (pair: { left: Record<string, unknown>; right: Record<string, unknown> }) =>
        matchingApi.test(graphId, nodeTypeId, pair.left, pair.right),
    }),
  }
}

export function useReviews(
  graphId: string | undefined,
  status = 'PENDING',
  nodeTypeId?: string,
) {
  return useQuery({
    // The node type is part of the key, or switching entity would serve the
    // previous entity's queue from cache.
    queryKey: [...brainKeys.reviews(graphId ?? '', status), nodeTypeId ?? 'all'],
    queryFn: () => matchingApi.reviews(graphId!, status, nodeTypeId),
    enabled: Boolean(graphId),
  })
}

export function useResolveReview(graphId: string) {
  return useMutation({
    mutationFn: (input: { reviewId: string; action: 'merge' | 'reject'; note?: string }) =>
      matchingApi.resolve(graphId, input.reviewId, input.action, input.note),
    onSuccess: () => invalidateData(graphId),
  })
}

/**
 * Apply declared links to records that are already in the graph.
 *
 * The pipeline wires links up as rows arrive; this is for the change of mind
 * afterwards, when the rows are in and the queue that carried them has drained.
 */
export function useRebuildRelationships(graphId: string) {
  return useMutation({
    mutationFn: () => recordsApi.rebuildRelationships(graphId),
    onSuccess: () => invalidateData(graphId),
  })
}

/** Send a rejected record back through the pipeline from stage one. */
export function useResubmitRecord(graphId: string) {
  return useMutation({
    mutationFn: (recordId: string) => recordsApi.resubmit(graphId, recordId),
    onSuccess: () => invalidateData(graphId),
  })
}

/** Correct a rejected record's values. It stays rejected until resubmitted. */
export function useEditStagedRecord(graphId: string) {
  return useMutation({
    mutationFn: (vars: { recordId: string; properties: Record<string, unknown> }) =>
      recordsApi.editStaged(graphId, vars.recordId, vars.properties),
    onSuccess: () => invalidateData(graphId),
  })
}

/** Score a waiting pair again after its records or the rules have changed. */
export function useResubmitReview(graphId: string) {
  return useMutation({
    mutationFn: (reviewId: string) => matchingApi.resubmit(graphId, reviewId),
    onSuccess: () => invalidateData(graphId),
  })
}

/** Correct a golden record's values in place. */
export function useUpdateRecord(graphId: string) {
  return useMutation({
    mutationFn: (vars: { entityId: string; properties: Record<string, unknown> }) =>
      recordsApi.update(graphId, vars.entityId, { properties: vars.properties }),
    onSuccess: () => invalidateData(graphId),
  })
}

// --- survivorship ------------------------------------------------------------

export function useStrategies(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.strategies(graphId ?? ''),
    queryFn: () => survivorshipApi.strategies(graphId!),
    enabled: Boolean(graphId),
    staleTime: 30 * 60_000,
  })
}

export function useSurvivorshipMutations(graphId: string, nodeTypeId: string) {
  const done = () => invalidateModel(graphId)
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) =>
        survivorshipApi.create(graphId, nodeTypeId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        survivorshipApi.update(graphId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => survivorshipApi.remove(graphId, id),
      onSuccess: done,
    }),
    test: useMutation({
      mutationFn: (candidates: Record<string, unknown>[]) =>
        survivorshipApi.test(graphId, nodeTypeId, candidates),
    }),
  }
}

export function useAudit(graphId: string | undefined, entityId?: string) {
  return useQuery({
    queryKey: brainKeys.audit(graphId ?? '', entityId),
    queryFn: () => survivorshipApi.audit(graphId!, entityId),
    enabled: Boolean(graphId),
  })
}

// --- records -----------------------------------------------------------------

export function useRecords(
  graphId: string | undefined,
  params: { node_type_key?: string; status?: string; q?: string; page?: number },
) {
  return useQuery({
    queryKey: brainKeys.records(graphId ?? '', params),
    queryFn: () => recordsApi.list(graphId!, params),
    enabled: Boolean(graphId),
  })
}

export function useStagedRecords(
  graphId: string | undefined,
  params: { status?: string; node_type_key?: string; page?: number },
) {
  return useQuery({
    queryKey: brainKeys.staged(graphId ?? '', params),
    queryFn: () => recordsApi.staged(graphId!, params),
    enabled: Boolean(graphId),
  })
}

/** Every count the Records screen shows, in one request. */
export function useRecordCounts(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.recordCounts(graphId ?? ''),
    queryFn: () => recordsApi.counts(graphId!),
    enabled: Boolean(graphId),
  })
}

// --- relationships between records -------------------------------------------

export function useRelationships(
  graphId: string | undefined,
  params: {
    edge_type_key?: string
    entity_id?: string
    node_type_key?: string
    q?: string
    page?: number
  },
) {
  return useQuery({
    queryKey: brainKeys.relationships(graphId ?? '', params),
    queryFn: () => relationshipsApi.list(graphId!, params),
    enabled: Boolean(graphId),
  })
}

export function useRelationshipSummary(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.relationshipSummary(graphId ?? ''),
    queryFn: () => relationshipsApi.summary(graphId!),
    enabled: Boolean(graphId),
  })
}

/** Did the declared links actually resolve? The diagnostic, not the data. */
export function useLinkReport(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.linkReport(graphId ?? ''),
    queryFn: () => relationshipsApi.links(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useEgo(graphId: string | undefined, entityId: string | undefined, depth = 1) {
  return useQuery({
    queryKey: brainKeys.ego(graphId ?? '', entityId ?? '', depth),
    queryFn: () => recordsApi.ego(graphId!, entityId!, depth),
    enabled: Boolean(graphId && entityId),
  })
}

export function useLineage(graphId: string | undefined, entityId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.lineage(graphId ?? '', entityId ?? ''),
    queryFn: () => recordsApi.lineage(graphId!, entityId!),
    enabled: Boolean(graphId && entityId),
  })
}

// --- objects -----------------------------------------------------------------

export function useObjectTables(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.objectTables(graphId ?? ''),
    queryFn: () => objectsApi.tables(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useObjectRows(
  graphId: string | undefined,
  nodeTypeId: string | undefined,
  params: { q?: string; object_id?: string; entity_id?: string; page?: number },
) {
  return useQuery({
    queryKey: brainKeys.objectRows(graphId ?? '', nodeTypeId ?? '', params),
    queryFn: () => objectsApi.rows(graphId!, nodeTypeId!, params),
    enabled: Boolean(graphId && nodeTypeId),
    // A table that has just been created has no rows yet and that is not an
    // error worth retrying.
    retry: false,
  })
}

export function useRebuildObjects(graphId: string) {
  return useMutation({
    mutationFn: () => objectsApi.rebuild(graphId),
    onSuccess: () => invalidateData(graphId),
  })
}

// --- sources, agents, pipeline ----------------------------------------------

export function useSources(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.sources(graphId ?? ''),
    queryFn: () => sourcesApi.list(graphId!),
    enabled: Boolean(graphId),
    // The scheduler ticks once a minute and a pass moves the marks (forward
    // catch-up, backfill pages, next check). Poll the list while anything is
    // scheduled or mid-catch-up so those change on screen without a reload;
    // a graph with only batch sources costs nothing extra.
    refetchInterval: (query) => {
      const rows = query.state.data?.sources ?? []
      const live = rows.some(
        (r) =>
          r.sync_mode === 'poll' ||
          r.last_run_status === 'running' ||
          (r.sync ?? []).some((o) => Boolean(o.forward) || (o.paginated && !o.backfill_done)),
      )
      return live ? 15_000 : false
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

/** Step one of the source flow: connectors and the connections you have. */
export function useSourceConnectors(graphId: string | undefined) {
  return useQuery({
    queryKey: ['brain', 'source-connectors', graphId ?? ''],
    queryFn: () => sourcesApi.connectors(graphId!),
    enabled: Boolean(graphId),
  })
}

/** Step three: what one connection can be asked for. */
export function useConnectionObjects(graphId: string | undefined, connectionId: string | null) {
  return useQuery({
    queryKey: ['brain', 'connection-objects', graphId ?? '', connectionId ?? ''],
    queryFn: () => sourcesApi.objects(graphId!, connectionId!),
    enabled: Boolean(graphId && connectionId),
  })
}

export function usePreviewObject(graphId: string) {
  return useMutation({
    mutationFn: (body: {
      connection_id: string
      action_id: string
      input?: Record<string, unknown>
    }) => sourcesApi.preview(graphId, body),
  })
}

export function useSourceMutations(graphId: string) {
  const done = () => invalidateData(graphId)
  return {
    attach: useMutation({
      mutationFn: (body: Record<string, unknown>) => sourcesApi.attach(graphId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        sourcesApi.update(graphId, id, body),
      onSuccess: done,
    }),
    detach: useMutation({
      mutationFn: (id: string) => sourcesApi.detach(graphId, id),
      onSuccess: done,
    }),
    /** Every source on the graph, then one pipeline run over all of it. */
    publishAll: useMutation({
      mutationFn: () => sourcesApi.publishAll(graphId),
      onSuccess: done,
    }),
    publish: useMutation({
      mutationFn: (vars: { id: string }) => sourcesApi.publish(graphId, vars.id),
      onSuccess: done,
    }),
    resetSync: useMutation({
      mutationFn: (id: string) => sourcesApi.resetSync(graphId, id),
      onSuccess: done,
    }),
    ingest: useMutation({
      mutationFn: ({
        id,
        records,
        id_field,
        entity_hint,
      }: {
        id: string
        records: Record<string, unknown>[]
        id_field?: string
        entity_hint?: string
      }) => sourcesApi.ingest(graphId, id, { records, id_field, entity_hint }),
      onSuccess: done,
    }),
    runImport: useMutation({
      mutationFn: ({
        id,
        action_id,
        input,
        entity_hint,
      }: {
        id: string
        action_id: string
        input?: Record<string, unknown>
        entity_hint?: string
      }) => sourcesApi.runImport(graphId, id, { action_id, input, entity_hint }),
      onSuccess: done,
    }),
  }
}

/** What one source can be asked to import. Driven entirely by the manifest. */
export function useImportScopes(graphId: string, sourceRowId: string | null | undefined) {
  return useQuery({
    queryKey: [...brainKeys.sources(graphId), 'import-scopes', sourceRowId ?? ''],
    enabled: Boolean(sourceRowId),
    // The manifest is fixed per deploy, so there is nothing to poll for.
    staleTime: 15 * 60_000,
    queryFn: () => sourcesApi.importScopes(graphId, sourceRowId!),
  })
}

export function useAgents(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.agents(graphId ?? ''),
    queryFn: () => agentsApi.list(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useAgentMutations(graphId: string) {
  const done = () => queryClient.invalidateQueries({ queryKey: brainKeys.agents(graphId) })
  return {
    create: useMutation({
      mutationFn: (body: Record<string, unknown>) => agentsApi.create(graphId, body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        agentsApi.update(graphId, id, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => agentsApi.remove(graphId, id),
      onSuccess: done,
    }),
  }
}

export function usePipelineStatus(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.pipelineStatus(graphId ?? ''),
    queryFn: () => pipelineApi.status(graphId!),
    enabled: Boolean(graphId),
    refetchInterval: (query) => {
      // Poll only while there is work in flight; an idle graph should not be
      // generating a request every few seconds.
      const data = query.state.data
      const busy = data ? Object.values(data.queues).some((n) => n > 0) : false
      return busy ? 4000 : false
    },
  })
}

export function usePipelineRuns(graphId: string | undefined) {
  return useQuery({
    queryKey: brainKeys.pipelineRuns(graphId ?? ''),
    queryFn: () => pipelineApi.runs(graphId!),
    enabled: Boolean(graphId),
  })
}

export function useRunPipeline(graphId: string) {
  return useMutation({
    mutationFn: () => pipelineApi.run(graphId),
    onSuccess: () => invalidateData(graphId),
  })
}

export function useRetryArchived(graphId: string) {
  return useMutation({
    mutationFn: (messageId?: string) => pipelineApi.retry(graphId, messageId),
    onSuccess: () => invalidateData(graphId),
  })
}

// --- search and generation ---------------------------------------------------

export function useSearch(graphId: string | undefined, q: string) {
  return useQuery({
    queryKey: brainKeys.search(graphId ?? '', q),
    queryFn: () => searchApi.search(graphId!, q),
    enabled: Boolean(graphId && q.trim().length > 1),
  })
}

export function useGeneratorPreview() {
  return useMutation({
    mutationFn: ({ requirement, graphId }: { requirement: string; graphId?: string }) =>
      generatorApi.preview(requirement, graphId),
  })
}

export function useGenerate() {
  return useMutation({
    mutationFn: (body: {
      requirement: string
      name?: string
      proposal?: ModelProposal
      graphId?: string
    }) =>
      body.graphId
        ? generatorApi.generateInto(body.graphId, {
            requirement: body.requirement,
            proposal: body.proposal,
          })
        : generatorApi.generate(body),
    onSuccess: (result) => {
      void invalidateModel(result.graph_id)
      void queryClient.invalidateQueries({ queryKey: brainKeys.graphs })
    },
  })
}
